import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { getMe } from './api';

export type Role = 'developer' | 'reporter';

interface AuthValue {
  session: Session | null;
  email: string | null;
  role: Role | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      if (!data.session) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (!next) {
        setRole(null);
        setEmail(null);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // The role is the server's answer, not something the browser decides: it comes
  // from alerts.developers, checked against the verified session.
  useEffect(() => {
    if (!session) return;
    let alive = true;
    setLoading(true);
    getMe()
      .then((me) => {
        if (!alive) return;
        setRole(me.role);
        // owners hold the person's canonical e-mail, which can differ from the
        // address this session signed in with
        setEmail(me.person?.email ?? me.email);
        setError(null);
      })
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [session]);

  const signIn = useCallback(async (mail: string, password: string) => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email: mail.trim(), password });
    if (err) throw new Error(err.message);
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ session, email, role, loading, error, signIn, signOut }),
    [session, email, role, loading, error, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
